"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { type ExamType, EXAM_CONFIGS } from "@/lib/exam/config";
import {
  BookMarked, BarChart2, Search, ClipboardList, PenLine, BookCheck, HelpCircle,
  Calendar, CalendarDays, AlertTriangle, BookOpen, AudioLines, Mic2, Languages,
  Calculator, FileText, SpellCheck, Microscope, Target, GraduationCap, RefreshCw,
  DollarSign, Trophy, LayoutDashboard,
  type LucideIcon,
} from "lucide-react";

/**
 * ExamResourceSidebar — the left rail shown on every exam resource sub-page.
 *
 * Shows the exam header, Practice Exams link, all sibling resource links
 * (Study Guide, Scoring Guide, etc.), the optional leaderboard link, and —
 * when the page provides a TOC — an "On this page" section stacked below.
 *
 * Kept in sync with exam-hub-sidebar.tsx (same RESOURCE_LINKS order and
 * color map) so users see identical navigation on hub pages and sub-pages.
 */

const COLOR_MAP: Record<string, { activeBg: string; activeText: string; hoverBg: string; hoverText: string }> = {
  blue:   { activeBg: "bg-blue-50",   activeText: "text-blue-700",   hoverBg: "hover:bg-gray-50", hoverText: "hover:text-blue-700"   },
  green:  { activeBg: "bg-green-50",  activeText: "text-green-700",  hoverBg: "hover:bg-gray-50", hoverText: "hover:text-green-700"  },
  purple: { activeBg: "bg-purple-50", activeText: "text-purple-700", hoverBg: "hover:bg-gray-50", hoverText: "hover:text-purple-700" },
  indigo: { activeBg: "bg-indigo-50", activeText: "text-indigo-700", hoverBg: "hover:bg-gray-50", hoverText: "hover:text-indigo-700" },
  orange: { activeBg: "bg-orange-50", activeText: "text-orange-700", hoverBg: "hover:bg-gray-50", hoverText: "hover:text-orange-700" },
};

function getColors(color: string) {
  return COLOR_MAP[color] ?? COLOR_MAP.blue;
}

export const RESOURCE_LINKS = [
  { path: "/guide",            label: "Study Guide",      icon: BookMarked },
  { path: "/scoring-guide",    label: "Scoring Guide",    icon: BarChart2 },
  { path: "/question-types",   label: "Question Types",   icon: Search },
  { path: "/sample-questions", label: "Sample Questions", icon: ClipboardList },
  { path: "/writing-guide",    label: "Writing Guide",    icon: PenLine },
  { path: "/vocabulary",       label: "Vocabulary",       icon: BookCheck },
  { path: "/faq",              label: "FAQ",              icon: HelpCircle },
  { path: "/study-plan",       label: "Study Plan",       icon: Calendar },
  { path: "/test-day",         label: "Test Day Guide",   icon: CalendarDays },
  { path: "/mistakes",         label: "Common Mistakes",  icon: AlertTriangle },
] as const;

type NavLink = { path: string; label: string; icon: LucideIcon };

/** Skill deep-dive pages — each exam only shows its own sections */
const SKILL_LINKS: Partial<Record<ExamType, NavLink[]>> = {
  toefl: [
    { path: "/reading",   label: "Reading",   icon: BookOpen },
    { path: "/listening", label: "Listening", icon: AudioLines },
    { path: "/speaking",  label: "Speaking",  icon: Mic2 },
    { path: "/grammar",   label: "Grammar",   icon: Languages },
  ],
  sat: [
    { path: "/reading",   label: "Reading & Writing", icon: BookOpen },
    { path: "/math",      label: "Math",              icon: Calculator },
    { path: "/grammar",   label: "Grammar",           icon: PenLine },
    { path: "/formulas",  label: "Formula Sheet",     icon: FileText },
  ],
  act: [
    { path: "/english",  label: "English",  icon: PenLine },
    { path: "/math",     label: "Math",     icon: Calculator },
    { path: "/reading",  label: "Reading",  icon: BookOpen },
    { path: "/science",  label: "Science",  icon: Microscope },
    { path: "/grammar",  label: "Grammar",  icon: SpellCheck },
  ],
  ielts: [
    { path: "/reading",   label: "Reading",   icon: BookOpen },
    { path: "/listening", label: "Listening", icon: AudioLines },
    { path: "/speaking",  label: "Speaking",  icon: Mic2 },
    { path: "/grammar",   label: "Grammar",   icon: Languages },
  ],
  gre: [
    { path: "/verbal",    label: "Verbal",             icon: BookOpen },
    { path: "/quant",     label: "Quantitative",       icon: Calculator },
    { path: "/awa",       label: "Analytical Writing", icon: PenLine },
    { path: "/grammar",   label: "Grammar",            icon: SpellCheck },
    { path: "/formulas",  label: "Formula Sheet",      icon: FileText },
  ],
};

/** Score & planning pages — common to all 5 main exams */
const PLANNING_LINKS: NavLink[] = [
  { path: "/score-meaning",           label: "Score Meaning",     icon: Target },
  { path: "/university-requirements", label: "University Reqs",   icon: GraduationCap },
  { path: "/retake",                  label: "Retake Policy",     icon: RefreshCw },
  { path: "/accommodations",          label: "Accommodations",    icon: HelpCircle },
  { path: "/timeline",                label: "Test Timeline",     icon: CalendarDays },
  { path: "/fee-waiver",              label: "Fee Waiver",        icon: DollarSign },
];

const MAIN_EXAMS: ExamType[] = ["toefl", "sat", "act", "ielts", "gre"];

/** Pages that exist only under app/<main exam>/ — never link them from generic hubs (2026-09-13: 105 dead links). */
export const MAIN_ONLY_RESOURCE_PATHS: ReadonlySet<string> = new Set(["/study-plan", "/test-day", "/mistakes"]);

const LEADERBOARD_EXAMS: ExamType[] = ["toefl", "sat"];

interface ExamResourceSidebarProps {
  examType: ExamType;
  /** Optional table of contents for the current page. Tuple of [id, label]. */
  tocItems?: ReadonlyArray<readonly [string, string]>;
  /** Close handler used when the sidebar is rendered inside the mobile drawer. */
  onNavigate?: () => void;
  /** When true, skips the `hidden lg:block` wrapper (for use inside mobile drawer). */
  inDrawer?: boolean;
}

export function ExamResourceSidebar({
  examType,
  tocItems,
  onNavigate,
  inDrawer,
}: ExamResourceSidebarProps) {
  const pathname = usePathname();
  const config = EXAM_CONFIGS[examType];
  const colors = getColors(config.color);
  const base = `/${examType}`;
  const isHub = pathname === base || pathname === `${base}/`;

  const wrapperClass = inDrawer
    ? "w-full"
    : "w-56 flex-shrink-0 hidden lg:block";
  const stickyClass = inDrawer ? "" : "sticky top-24";

  return (
    <aside className={wrapperClass}>
      <div className={stickyClass}>
        {/* Exam header */}
        <div className="mb-4">
          <Link
            href={base}
            onClick={onNavigate}
            className={`flex items-center gap-2 text-lg font-bold transition-colors ${
              isHub ? colors.activeText : "text-gray-900"
            }`}
          >
            <config.icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.75} aria-hidden="true" />
            {config.name}
          </Link>
          <p className="text-xs text-gray-400 mt-1">
            {config.sections.filter(s => !s.placeholder).map(s => s.label).join(" · ")}
          </p>
        </div>

        {/* Practice Exams */}
        <Link
          href={base}
          onClick={onNavigate}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors mb-1
            ${isHub ? `${colors.activeBg} ${colors.activeText}` : `text-gray-600 ${colors.hoverBg}`}`}
        >
          <Target className="w-4 h-4" strokeWidth={1.75} aria-hidden="true" />
          Practice Exams
        </Link>

        {/* Resources */}
        <div className="mt-4 mb-2 px-3">
          <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
            Resources
          </span>
        </div>
        <nav className="space-y-0.5">
          {RESOURCE_LINKS.filter(link => MAIN_EXAMS.includes(examType) || !MAIN_ONLY_RESOURCE_PATHS.has(link.path)).map(link => {
            const href = `${base}${link.path}`;
            const isActive = pathname === href;
            return (
              <Link
                key={link.path}
                href={href}
                onClick={onNavigate}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors
                  ${isActive
                    ? `${colors.activeBg} ${colors.activeText} font-medium`
                    : `text-gray-600 ${colors.hoverBg}`}`}
              >
                <link.icon className="w-4 h-4" strokeWidth={1.75} aria-hidden="true" />
                {link.label}
              </Link>
            );
          })}
        </nav>

        {/* Skills — exam-specific deep-dive pages */}
        {SKILL_LINKS[examType] && (
          <>
            <div className="mt-4 mb-2 px-3">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                Skills
              </span>
            </div>
            <nav className="space-y-0.5">
              {SKILL_LINKS[examType]!.map(link => {
                const href = `${base}${link.path}`;
                const isActive = pathname === href;
                return (
                  <Link
                    key={link.path}
                    href={href}
                    onClick={onNavigate}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors
                      ${isActive
                        ? `${colors.activeBg} ${colors.activeText} font-medium`
                        : `text-gray-600 ${colors.hoverBg}`}`}
                  >
                    <link.icon className="w-4 h-4" strokeWidth={1.75} aria-hidden="true" />
                    {link.label}
                  </Link>
                );
              })}
            </nav>
          </>
        )}

        {/* Score & Planning — common to all 5 main exams */}
        {MAIN_EXAMS.includes(examType) && (
          <>
            <div className="mt-4 mb-2 px-3">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                Score &amp; Planning
              </span>
            </div>
            <nav className="space-y-0.5">
              {PLANNING_LINKS.map(link => {
                const href = `${base}${link.path}`;
                const isActive = pathname === href;
                return (
                  <Link
                    key={link.path}
                    href={href}
                    onClick={onNavigate}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors
                      ${isActive
                        ? `${colors.activeBg} ${colors.activeText} font-medium`
                        : `text-gray-600 ${colors.hoverBg}`}`}
                  >
                    <link.icon className="w-4 h-4" strokeWidth={1.75} aria-hidden="true" />
                    {link.label}
                  </Link>
                );
              })}
            </nav>
          </>
        )}

        {/* Community (only for exams with leaderboard data) */}
        {LEADERBOARD_EXAMS.includes(examType) && (
          <>
            <div className="mt-4 mb-2 px-3">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                Community
              </span>
            </div>
            <Link
              href={`${base}/leaderboard`}
              onClick={onNavigate}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors
                ${pathname === `${base}/leaderboard`
                  ? `${colors.activeBg} ${colors.activeText} font-medium`
                  : `text-gray-600 ${colors.hoverBg}`}`}
            >
              <Trophy className="w-4 h-4" strokeWidth={1.75} aria-hidden="true" />
              Leaderboard
            </Link>
          </>
        )}

        {/* On this page (TOC) — only when the current page provides items */}
        {tocItems && tocItems.length > 0 && (
          <div className="mt-6 border-t border-gray-100 pt-4">
            <div className="mb-2 px-3">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                On this page
              </span>
            </div>
            <nav className="space-y-0.5 text-sm">
              {tocItems.map(([id, label]) => (
                <a
                  key={id}
                  href={`#${id}`}
                  onClick={onNavigate}
                  className={`block px-3 py-1 text-gray-500 ${colors.hoverText} transition-colors truncate`}
                >
                  {label}
                </a>
              ))}
            </nav>
          </div>
        )}

        {/* Dashboard */}
        <div className="mt-6 border-t border-gray-100 pt-4">
          <Link
            href="/dashboard"
            onClick={onNavigate}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <LayoutDashboard className="w-4 h-4" strokeWidth={1.75} aria-hidden="true" />
            Dashboard
          </Link>
        </div>
      </div>
    </aside>
  );
}
